import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Table } from "antd";
import {
  getEmployeesResignations,
  getSalaryHoldTypes,
  resignationApproval,
} from "../../services/ResignationServices";
import Breadcrumbs from "../Breadcrumbs";
import { formatDateInTimezone } from "../../utils/formatDateInTimezone";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import RowDetailModal from "../RowDetailModal";
import { companyID } from "../../auth";
import Swal from "sweetalert2";
import Loader from "../Loader";
import { parseISO } from "date-fns";
import { encrypt } from "../../utils/crypto";

const EmployeeResignation = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState({
    show: false,
    message: "",
  });
  const [employeeResignations, setEmployeeResignations] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [salaryHoldTypes, setSalaryHoldTypes] = useState([]);
  const [decisionRemarksError, setDecisionRemarksError] = useState({});
  const [decisionRemarks, setDecisionRemarks] = useState("");

  const employeeId = localStorage.getItem("employeeID");
  const company_id = companyID();

  const fetchEmployeesResignations = async () => {
    if (company_id && employeeId) {
      const filterKey = "approved_by";
      const filterValue = employeeId;
      try {
        const response = await getEmployeesResignations(
          company_id,
          filterKey,
          filterValue
        );
        setEmployeeResignations(response?.result);
      } catch (err) {
        console.error(err);
      }
    }
  };

  const fetchSalaryHoldTypes = async () => {
    try {
      const response = await getSalaryHoldTypes();
      setSalaryHoldTypes(response.result);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchEmployeesResignations();
    fetchSalaryHoldTypes();
  }, []);

  const onRowClick = (record) => {
    setSelectedRow(record);
    setIsModalVisible(true);
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setSelectedRow(null);
    setExpanded(false);
    setDecisionRemarksError({});
    setDecisionRemarks("");
  };

  const submitResignationApproval = async (approvalId, approvalData) => {
    setLoading((prev) => ({
      ...prev,
      show: true,
      message: `${
        approvalData?.approval_status == 2 ? "Approving" : "Rejecting"
      } separation...`,
    }));
    try {
      await resignationApproval(approvalId, approvalData);
      Swal.fire({
        title: "Separation",
        text: `Separation ${
          approvalData?.approval_status == 2 ? "approved" : "rejected"
        }`,
        icon: "success",
        confirmButtonText: "OK",
        timer: 2000,
        timerProgressBar: true,
        customClass: {
          confirmButton: "btn btn-primary",
        },
      });
      closeModal();
      fetchEmployeesResignations();
    } catch (error) {
      console.error(error);
      Swal.fire({
        title: "Separation",
        text: "Something went wrong, please try again later !",
        icon: "warning",
        confirmButtonText: "OK",
        timer: 2000,
        timerProgressBar: true,
        customClass: {
          confirmButton: "btn btn-primary",
        },
      });
    } finally {
      setLoading((prev) => ({
        ...prev,
        show: false,
        message: "",
      }));
    }
  };

  const approvalAction = async (action, record) => {
    const encryptedEmployeeId = encrypt(record?.employee_id);
    const approvalData = {
      resignation_date: formatDateInTimezone(record?.resignation_date),
      last_working_date: formatDateInTimezone(record?.last_working_date),
      requestor_id: record?.employee_id,
      employees_resignations_id: record?.employees_resignations_id,
      approval_status: action?.key,
      employee_id: employeeId,
    };
    if (isModalVisible && action?.key !== 4) {
      if (!decisionRemarks) {
        setDecisionRemarksError((prev) => ({
          ...prev,
          [record.key]: "Please give a valid decision Remarks.",
        }));
        return;
      } else if (decisionRemarks.length < 5) {
        setDecisionRemarksError((prev) => ({
          ...prev,
          [record.key]: "Please write more for decision Remarks.",
        }));
        return;
      }
      try {
        await submitResignationApproval(
          record?.approval_history[0]?.approval_id,
          { ...approvalData, remarks: decisionRemarks }
        );
      } catch (error) {
        console.error(error);
      }
    } else if (isModalVisible && action?.key === 4) {
      navigate(`../employees/profile/${encryptedEmployeeId}`, {
        state: {
          activeTab: "emp_resignation",
        },
      });
    } else {
      onRowClick(record);
    }
  };

  const approvalActions = [
    { value: "Approve", key: 2 },
    { value: "Reject", key: 3 },
    { value: "Edit", key: 4 },
  ];
  const [expanded, setExpanded] = useState(false);

  const truncateText = (text, wordLimit) => {
    const words = text?.split(" ");
    if (words?.length > wordLimit) {
      return words.slice(0, wordLimit).join(" ") + "...";
    }
    return text;
  };

  function checkApprover(resignationData) {
    if (
      !Array.isArray(resignationData?.approval_history) ||
      resignationData?.approval_history?.length === 0
    ) {
      return {
        approvalPermission: false,
        isEditable: false,
      };
    }

    const approvalPermission = resignationData?.approval_history?.some(
      (approval) => approval?.approved_by == employeeId
    );

    const editPermission = resignationData?.resignation_status !== 3;

    return {
      approvalPermission: approvalPermission,
      isEditable: editPermission,
    };
  }

  const columns = [
    {
      title: "Employee Name",
      dataIndex: "employee_name",
      render: (text, record) => (
        <span
          className="table-avatar"
          style={{ cursor: "pointer" }}
          onClick={() => onRowClick(record)}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Separation date",
      dataIndex: "resignation_date",
      key: "resignation_date",
      render: (text, record) => (
        <span onClick={() => onRowClick(record)}>
          {formatDateInTimezone(text)}
        </span>
      ),
    },
    {
      title: "Notice period",
      dataIndex: "notice_period_days",
      key: "notice_period_days",
      render: (text, record) => (
        <span onClick={() => onRowClick(record)}>{`${text} day(s)`}</span>
      ),
    },
    {
      title: "Last working date",
      dataIndex: "last_working_date",
      key: "last_working_date",
      render: (text, record) => (
        <span onClick={() => onRowClick(record)}>
          {formatDateInTimezone(text)}
        </span>
      ),
    },
    {
      title: "Separation status",
      dataIndex: "resignation_status",
      key: "resignation_status",
      render: (text, record) => (
        <span
          className={`badge bg-${
            text == 1 ? "warning" : text == 2 ? "success" : "danger"
          }`}
          onClick={() => onRowClick(record)}
        >
          {text == 1 ? "Pending" : text == 2 ? "Approved" : "Rejected"}
        </span>
      ),
    },
    {
      title: "Approval status",
      dataIndex: "approval_status",
      key: "approval_status",
      render: (text, record) => (
        <span
          className={`badge bg-${
            record?.approval_history[0]?.approval_status == 1
              ? "warning"
              : record?.approval_history[0]?.approval_status == 2
              ? "success"
              : "danger"
          }`}
        >
          {record?.approval_history[0]?.approval_status == 1
            ? "Pending"
            : record?.approval_history[0]?.approval_status == 2
            ? "Approved"
            : "Rejected"}
        </span>
      ),
    },
    ...(isModalVisible
      ? [
          {
            title: "Salary hold",
            dataIndex: "salary_hold_type_id",
            key: "salary_hold_type_id",
            render: (text) =>
              salaryHoldTypes.find((type) => type.id == text)?.name || "-",
          },
        ]
      : []),
    ...(isModalVisible
      ? [
          {
            title: "Final Decision Remarks",
            dataIndex: "resignation_status",
            render: (text, record) => record.remarks ?? "-",
          },
        ]
      : []),
    ...(isModalVisible
      ? [
          {
            title: "Give Remarks",
            dataIndex: "resignation_status",
            render: (text, record) => (
              <>
                <textarea
                  className="form-control"
                  placeholder="Enter Decision Remarks"
                  value={decisionRemarks}
                  onChange={(e) => {
                    setDecisionRemarks(e.target.value);
                    setDecisionRemarksError((prev) => ({
                      ...prev,
                      [record.key]: "",
                    }));
                  }}
                />
                {decisionRemarksError[record.key] && (
                  <span className="text-danger">
                    * {decisionRemarksError[record.key]}
                  </span>
                )}
              </>
            ),
          },
        ]
      : []),
    {
      title: "Reason of separation",
      dataIndex: "reason_of_resignation",
      key: "reason_of_resignation",
      render: (text, record) => (
        <>
          {expanded ? text : truncateText(text, 10)}
          {text?.split(" ").length > 10 && (
            <button
              className="btn btn-link text-primary"
              onClick={() => {
                onRowClick(record);
                setExpanded(!expanded);
              }}
              style={{
                paddingTop: "0px",
                fontSize: "14px",
              }}
            >
              {expanded ? "Read Less" : "Read More"}
            </button>
          )}
        </>
      ),
    },
    {
      title: "Action",
      render: (text, record) => {
        const { approvalPermission, isEditable } = checkApprover(record);
        return (
          <div className="d-flex gap-3 mt-2">
            {approvalPermission &&
              approvalActions?.map((action) => {
                return (
                  <button
                    disabled={  record?.employment_status !== "separated" ?
                      ( action.key === record?.resignation_status)
                        ? true
                        : false : true
                    }
                    className={`${
                      isModalVisible
                        ? `btn btn-outline-${  record?.employment_status !== "separated"  ?
                            action.key === record?.resignation_status 
                              ? "light"
                              : action.key === 2
                              ? "success"
                              : action.key === 4
                              ? "info"
                              : "danger" : "secondary"
                          }`
                        : "dropdown-item"
                    }`}
                    onClick={() => approvalAction(action, record)}
                  >
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip
                          id="tooltip-view"
                          style={{ fontSize: "12px", padding: "2px" }}
                        >
                          {action.value} Resignation
                        </Tooltip>
                      }
                    >
                      <span>
                        {isModalVisible ? (
                          action.value
                        ) : (
                          <i
                            className={`fa-regular fa-${
                              action.key === 2
                                ? "circle-check"
                                : action.key === 4
                                ? "pen-to-square"
                                : "circle-xmark"
                            } ${
                               record?.employment_status !== "separated"  ?  
                              action.key === record?.resignation_status
                                ? "text-muted"
                                : action.key === 2
                                ? "text-success"
                                : action.key === 4
                                ? "text-info"
                                : "text-danger" :  "text-muted"
                            } ${isModalVisible ? "" : "m-r-5"}`}
                          />
                        )}
                      </span>
                    </OverlayTrigger>
                  </button>
                );
              })}
          </div>
        );
      },
    },
  ];
  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <Breadcrumbs
          maintitle="Employees Separation"
          title="Dashboard"
          subtitle="Employees Separation"
          titleLink="employees-resignation"
        />

        <div className="row">
          <div className="col-md-12">
            <div className="table-responsive">
              <Table
                scroll={{ x: 1000 }}
                className="table-striped"
                columns={columns}
                dataSource={employeeResignations?.reverse()}
                rowKey={(record) => record.id}
                loading={loading.show}
              />
            </div>
          </div>
        </div>

        {selectedRow && (
          <RowDetailModal
            visible={isModalVisible}
            onClose={closeModal}
            record={selectedRow}
            columns={columns}
            title="Separation details"
          />
        )}
      </div>
      <Loader show={loading.show} loadingMessage={loading.message} />
    </div>
  );
};

export default EmployeeResignation;
